import { cn } from "@/lib/utils";

export interface TogglePillOption {
  value: string;
  label: string;
}

export interface TogglePillProps {
  /** Array of toggle options */
  options: TogglePillOption[];
  /** Currently selected value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Additional className */
  className?: string;
}

/**
 * molecule-toggle-pill
 * 
 * A pill-shaped toggle control for binary/multi-option selection.
 * Follows Apple-style toggle aesthetics.
 * 
 * Tokens applied:
 * - Container: bg-muted rounded-full p-1
 * - Active: bg-background text-foreground shadow-sm rounded-full
 * - Inactive: text-muted-foreground hover:text-foreground
 * - Typography: text-sm font-medium
 * - Transition: duration-300 ease-out
 */
export function TogglePill({
  options,
  value,
  onChange,
  className = "",
}: TogglePillProps) {
  return (
    <div className={cn("inline-flex items-center rounded-full p-1 bg-muted", className)}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "min-w-[140px] px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 text-center",
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default TogglePill;
